import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import AddedToCartNotice from "../components/shop/AddedToCartNotice";
import { addToCart as trackAddToCart } from "../utils/analytics";
import { cartLineDecreaseAction, cartLineIncreaseAction } from "../utils/cartLineSteps";
import {
  CATALOG_ACTIONS,
  addCatalogProductToCart,
  catalogActionLabel,
  catalogActionOf,
  catalogCartKind,
  catalogCartQuantity,
  catalogMinQty,
  catalogPlainLine,
  catalogProductPath,
  catalogStorePath,
} from "../utils/cartRouting";

/**
 * useCatalogProductCart
 * ----------------------------------------------------------------------------
 * Everything a /shop grid card needs to sell its product where the shopper
 * is: what its button says and does, how many are in the vendor's cart, plus
 * and minus on that line, and the product and store addresses. The rules are
 * in utils/cartRouting.js and the server's verdict on each row; this hook
 * only wires them to the store, the router and the toast.
 * ----------------------------------------------------------------------------
 */
export default function useCatalogProductCart(product) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [busy, setBusy] = useState(false);

  const action = catalogActionOf(product);
  const minQty = catalogMinQty(product);
  const kind = catalogCartKind(product);
  const cartKey = product?.vendorId ? String(product.vendorId) : null;
  const quantity = useSelector((state) => catalogCartQuantity(state, product));
  const plainLine = useSelector((state) => catalogPlainLine(state, product));
  const productPath = catalogProductPath(product);
  const storePath = catalogStorePath(product);
  const stock = Number(product?.countInStock) || 0;

  const openProduct = () => {
    if (productPath) history.push(productPath);
  };

  const add = async () => {
    if (action === CATALOG_ACTIONS.CHOOSE || action === CATALOG_ACTIONS.QUOTE) {
      openProduct();
      return;
    }
    if (action !== CATALOG_ACTIONS.ADD || busy || !product) return;
    setBusy(true);
    try {
      const result = await dispatch(addCatalogProductToCart(product));
      if (result.added) {
        trackAddToCart({
          id: product._id,
          name: product.name,
          price: result.price ?? product.price,
          quantity: result.quantity || 1,
          vendorName: result.vendorName,
        });
        toast.success(
          <AddedToCartNotice name={product.name} vendorName={result.vendorName} quantity={result.quantity} />,
          { toastId: `catalog-added-${product._id}` }
        );
      } else if (result.action === CATALOG_ACTIONS.CHOOSE) {
        toast.info(`Choose a size or option for ${product.name} first.`);
        openProduct();
      } else if (result.action === CATALOG_ACTIONS.QUOTE) {
        openProduct();
      } else {
        toast.warning(
          result.reason
            ? `${product.name} cannot be added: ${String(result.reason).toLowerCase()}.`
            : `${product.name} cannot be added right now.`
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "We could not add this item. Check your connection and try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const increase = () => {
    if (plainLine && quantity < stock) dispatch(cartLineIncreaseAction(kind, cartKey, plainLine));
  };
  const decrease = () => {
    if (plainLine) dispatch(cartLineDecreaseAction(kind, cartKey, plainLine, { minQty }));
  };

  return {
    action,
    label: catalogActionLabel(product),
    minQty,
    quantity,
    // The grid steps the plain line only; a product sold in sizes is changed
    // in the cart or on its page, where the size shows.
    steppable: action === CATALOG_ACTIONS.ADD && Boolean(plainLine) && quantity > 0,
    canIncrease: quantity < stock,
    busy,
    add,
    increase,
    decrease,
    openProduct,
    productPath,
    storePath,
  };
}
